import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loading from '../../components/Loading';
import api from '../../api'; // pastikan ada file ini untuk axios instance
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { useSearch } from '../../hooks/useSearch';
import Pagination from '../../components/common/Pagination';
import { useAuth } from '../../contexts/AuthProvider';

const StandardDetail = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canStandardDetail = (key) => can(key, 'standardDetail');

  const { id } = useParams();
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [annexLoading, setAnnexLoading] = useState(false);
  const [clausulLoading, setClausulLoading] = useState(false);

  // Data states
  const [standardData, setStandardData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [annexes, setAnnexes] = useState([]);
  const [clausuls, setClausuls] = useState([]);
  const [servicesMaster, setServicesMaster] = useState([]);

  // Form states
  const [isAddingVersion, setIsAddingVersion] = useState(false);
  const [isAddingAnnex, setIsAddingAnnex] = useState(false);
  const [isAddingClausul, setIsAddingClausul] = useState(false);
  const [newVersion, setNewVersion] = useState({ version: '', publishedDate: '' });
  const [newAnnex, setNewAnnex] = useState({
    name: '', code: '', annex: '', description: '', version: ''
  });
  const [newClausul, setNewClausul] = useState({
    clausul: '', code: '', name: '', description: '', version: ''
  });

  // Selection states
  const [selectedVersionIds, setSelectedVersionIds] = useState([]);
  const [selectedAnnexIds, setSelectedAnnexIds] = useState([]);
  const [selectedClausulIds, setSelectedClausulIds] = useState([]);

  // Search states
  const [versionSearchTerm, setVersionSearchTerm] = useState('');
  const [annexSearchTerm, setAnnexSearchTerm] = useState('');
  const [clausulSearchTerm, setClausulSearchTerm] = useState('');

  // Total counts
  const [totalAnnexes, setTotalAnnexes] = useState(0);
  const [totalClausuls, setTotalClausuls] = useState(0);

  // Pagination
  const versionPagination = usePagination(10);
  const annexPagination = usePagination(10);
  const clausulPagination = usePagination(10);

  // Search hooks
  const { search: versionSearch, handleSearchChange: handleVersionSearchChange } = useSearch((value) => {
    versionPagination.setPage(0);
    fetchVersions(value);
  });

  const { search: annexSearch, handleSearchChange: handleAnnexSearchChange } = useSearch((value) => {
    annexPagination.setPage(0);
    fetchAnnexes(value);
  });

  const { search: clausulSearch, handleSearchChange: handleClausulSearchChange } = useSearch((value) => {
    clausulPagination.setPage(0);
    fetchClausuls(value);
  });

  // History data for the history section
  const historyData = [
    {
      avatar: { type: 'text', value: 'JL' },
      message: '<strong>Jeffie Lewzey</strong> commented on your <strong>"I\'m not a witch."</strong> post.',
      time: '24 hours ago',
      badge: true
    },
    {
      avatar: { type: 'image', value: './static/avatars/002m.jpg' },
      message: 'It\'s <strong>Mallory Hulme</strong>\'s birthday. Wish him well!',
      time: 'now',
      badge: true
    },
    {
      avatar: { type: 'image', value: './static/avatars/003m.jpg' },
      message: '<strong>Dunn Slane</strong> posted <strong>"Well, what do you want?"</strong>.',
      time: 'now',
      badge: true
    },
    {
      avatar: { type: 'image', value: './static/avatars/000f.jpg' },
      message: '<strong>Emmy Levet</strong> created a new project <strong>Morning alarm clock</strong>.',
      time: '4 days ago',
      badge: true
    },
    {
      avatar: { type: 'image', value: './static/avatars/001f.jpg' },
      message: '<strong>Maryjo Lebarree</strong> liked your photo.',
      time: 'now',
      badge: false
    }
  ];

  // Function to determine badge color based on invoice status
  const getBadgeClass = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-success-lt'; // Green for paid
      case 'Pending':
        return 'bg-warning-lt'; // Yellow for pending
      case 'Overdue':
        return 'bg-danger-lt'; // Red for overdue
      default:
        return 'bg-secondary-lt'; // Default color
    }
  };

  // Modal states
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [failMessage, setFailMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [currentModal, setCurrentModal] = useState(null); // 'version', 'annex', or 'clausul'
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [annexTotalPages, setAnnexTotalPages] = useState(0);

  const [annexSize, setAnnexSize] = useState(10);

  const [annexPage, setAnnexPage] = useState(0);

  // Fetch versions function
  const fetchVersions = async (searchValue = versionSearch) => {
    try {
      const response = await api.get(`/md/standards-versions/${id}`, {
        params: {
          page: versionPagination.page,
          size: versionPagination.size,
          search: searchValue
        }
      });
      const data = response.data || [];
      setVersions(data);
      versionPagination.setTotal(data.length);
      versionPagination.setCurrentPageItems(data.length);
      versionPagination.setTotalPages(1);
    } catch (error) {
      setVersions([]);
      versionPagination.setTotal(0);
      versionPagination.setCurrentPageItems(0);
      versionPagination.setTotalPages(0);
      handleApiError(error, 'Failed to fetch versions.');
    }
  };

  // Fetch annexes function
  const fetchAnnexes = async (searchValue = annexSearch) => {
    try {
      setAnnexLoading(true);
      const res = await api.get(`/md/standards/${id}/annexes`, {
        params: {
          search: searchValue,
          page: annexPagination.page,
          size: annexPagination.size,
        }
      });

      const { content, totalPages, totalElements } = res.data;
      setAnnexes(content || []);
      annexPagination.setTotal(totalElements || 0);
      annexPagination.setCurrentPageItems(content?.length || 0);
      annexPagination.setTotalPages(totalPages || 0);
    } catch (err) {
      setAnnexes([]);
      annexPagination.setTotal(0);
      annexPagination.setCurrentPageItems(0);
      annexPagination.setTotalPages(0);
      handleApiError(err, 'Failed to fetch annexes.');
    } finally {
      setAnnexLoading(false);
    }
  };

  // Fetch clausuls function
  const fetchClausuls = async (searchValue = clausulSearch) => {
    try {
      setClausulLoading(true);
      const res = await api.get(`/md/standards/${id}/clauses-page`, {
        params: { 
          search: searchValue, 
          page: clausulPagination.page, 
          size: clausulPagination.size 
        }
      });
      const { content, totalPages, totalElements } = res.data;
      setClausuls(content || []);
      clausulPagination.setTotal(totalElements || 0);
      clausulPagination.setCurrentPageItems(content?.length || 0);
      clausulPagination.setTotalPages(totalPages || 0);
    } catch (err) {
      setClausuls([]);
      clausulPagination.setTotal(0);
      clausulPagination.setCurrentPageItems(0);
      clausulPagination.setTotalPages(0);
      handleApiError(err, 'Failed to fetch clausuls.');
    } finally {
      setClausulLoading(false);
    }
  };

  // Helper function for handling API errors
  const handleApiError = (error, defaultMessage) => {
    let errorMessage = defaultMessage;
    let technicalDetails = '';

    if (error.response) {
      switch (error.response.status) {
        case 403:
          errorMessage = 'You do not have permission to view this data.';
          technicalDetails = 'Required role or permission is missing';
          break;
        case 500:
          errorMessage = 'Server encountered an error while fetching data.';
          technicalDetails = error.response.data.message || 'Internal server error occurred';
          break;
        default:
          errorMessage = 'An error occurred while fetching data.';
          technicalDetails = `Server responded with status: ${error.response.status}`;
      }
    } else if (error.request) {
      errorMessage = 'Unable to connect to the server.';
      technicalDetails = 'Network error or server is not responding';
    } else {
      errorMessage = 'An unexpected error occurred.';
      technicalDetails = error.message;
    }

    setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
    setShowFailModal(true);
  };

  // Update useEffect dependencies for each table
  useEffect(() => {
    if (id) {
      fetchVersions(versionSearch);
    }
  }, [id, versionSearch, versionPagination.page, versionPagination.size]);

  useEffect(() => {
    if (id) {
      fetchAnnexes(annexSearch);
    }
  }, [id, annexSearch, annexPagination.page, annexPagination.size]);

  useEffect(() => {
    if (id) {
      fetchClausuls(clausulSearch);
    }
  }, [id, clausulSearch, clausulPagination.page, clausulPagination.size]);

  useEffect(() => {
    console.log("Initial useEffect triggered with id:", id);
    setLoading(true);
    // Fetch all data in parallel
    Promise.allSettled([
      api.get(`/md/standards/${id}`),
      api.get(`/md/standards/${id}/annexes`),
      api.get(`/md/standards/${id}/clauses`),
      api.get(`/md/services`)
    ])
      .then((results) => {
        console.log("Promise.allSettled results:", results);
        const [standardRes, annexRes, clauseRes, servicesRes] = results;

        if (standardRes.status === 'fulfilled') {
          console.log("Standard data loaded:", standardRes.value.data);
          setStandardData(standardRes.value.data);
        }

        if (annexRes.status === 'fulfilled') {
          console.log("Annexes data loaded:", annexRes.value.data);
          const data = annexRes.value.data;
          if (Array.isArray(data)) {
            setAnnexes(data);
          } else if (data && Array.isArray(data.content)) {
            setAnnexes(data.content);
          } else {
            setAnnexes([]);
          }
        }

        if (clauseRes.status === 'fulfilled') {
          console.log("Clausuls data loaded:", clauseRes.value.data);
          setClauses(clauseRes.value.data || []);
        }

        if (servicesRes.status === 'fulfilled') {
          console.log("Services data loaded:", servicesRes.value.data);
          setServicesMaster(servicesRes.value.data);
        }
      })
      .catch((error) => {
        console.error("Error in Promise.allSettled:", error);
        setStandardData(null);
      })
      .finally(() => setLoading(false));

    // Fetch versions separately
    fetchVersions();
  }, [id]);

  useEffect(() => {
    console.log('Clauses State:', clauses); // Log the state
  }, [clauses]);

  // Add useEffect to monitor annexes state changes
  useEffect(() => {
    console.log('Annexes State changed:', annexes);
  }, [annexes]);

  const handleAddVersion = () => {
    if (!canStandardDetail('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    setCurrentModal('version');
    setModalMode('add');
    setNewVersion({ version: '' });
    if (window.bootstrap && window.bootstrap.Modal) {
      const modal = new window.bootstrap.Modal(document.getElementById('modal-form'));
      modal.show();
    }
  };

  const handleSelectAllVersions = (e) => {
    if (e.target.checked) {
      setSelectedVersionIds(versions.map(v => v.id));
    } else {
      setSelectedVersionIds([]);
    }
  };

  const handleEditVersion = () => {
    if (!canStandardDetail('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    if (selectedVersionIds.length !== 1) {
      setFailMessage('Please select exactly one version to edit.');
      setShowFailModal(true);
      return;
    }
    const selectedId = selectedVersionIds[0];
    const selectedVersion = versions.find(v => v.id === selectedId);
    if (selectedVersion) {
      setCurrentModal('version');
      setModalMode('edit');
      setNewVersion({ version: selectedVersion.version });
      if (window.bootstrap && window.bootstrap.Modal) {
        const modal = new window.bootstrap.Modal(document.getElementById('modal-form'));
        modal.show();
      }
    }
  };

  const handleDeleteVersion = () => {
    if (!canStandardDetail('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (selectedVersionIds.length === 0) {
      setFailMessage('Please select at least one version to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        for (const versionId of selectedVersionIds) {
          await api.delete(`/md/standards-versions/${versionId}`);
        }
        setSelectedVersionIds([]);
        const res = await api.get(`/md/standards-versions/${id}`);
        setVersions(res.data);
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected versions have been successfully deleted.');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete versions.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more versions not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  const handleSaveVersion = async () => {
    try {
      const payload = {
        version: newVersion.version,
        standard: { id: Number(id) }
      };
      await api.post(`/md/standards-versions`, payload);
      // Refresh versions after adding new one
      const res = await api.get(`/md/standards-versions/${id}`);
      setVersions(res.data);
      setIsAddingVersion(false);
      setNewVersion({ version: '', publishedDate: '' });
      setSuccessMessage('Version has been successfully added.');
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to save version", err);
      setFailMessage(`Failed to save version: ${err.message}`);
      setShowFailModal(true);
    }
  };

  const handleUpdateVersion = async () => {
    try {
      const selectedId = selectedVersionIds[0];
      const versionToEdit = versions.find(v => v.id === selectedId);
      await api.put(`/md/standards-versions/${selectedId}`, {
        ...versionToEdit,
        version: newVersion.version,
        standard: { id: Number(id) }
      });
      // Refresh versions after update
      const res = await api.get(`/md/standards-versions/${id}`);
      setVersions(res.data);
      setSelectedVersionIds([]);
      setNewVersion({ version: '' });
      setSuccessMessage('Version has been successfully updated.');
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to update version", err);
      setFailMessage(`Failed to update version: ${err.message}`);
      setShowFailModal(true);
      throw err;
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!standardData) {
    return (
      <div className="page-wrapper">
        <div className="container-xl py-4">
          <div className="text-center">
            <div className="alert alert-danger">
              Standard not found. <a href="#" onClick={() => navigate('/masterdata-standard')}>Return to standards list</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleAddAnnex = () => {
    setCurrentModal('annex');
    setModalMode('add');
    setNewAnnex({ name: '', code: '', annex: '', description: '', version: '' });
    const modal = new window.bootstrap.Modal(document.getElementById('modal-form'));
    modal.show();
  };

  const handleSaveAnnex = async () => {
    try {
      const payload = {
        name: newAnnex.name,
        annex: newAnnex.annex,
        code: newAnnex.code,
        description: newAnnex.description,
        standard: { id: Number(id) },
        // version: newAnnex.version ? { id: Number(newAnnex.version) } : null
        standardVersion: newAnnex.version ? { id: Number(newAnnex.version) } : null
      };
      // alert(JSON.stringify(payload, null, 2)); // Debugging line
      console.log("Saving annex with payload:", payload);
      const saveResponse = await api.post(`/md/annexes`, payload);
      console.log("Annex saved successfully:", saveResponse.data);
      console.log("Annex saved successfully, now fetching updated data...");

      // Fetch updated data
      await fetchAnnexes();
      console.log("Annex data refreshed");

      setIsAddingAnnex(false);
      setNewAnnex({ name: '', code: '', annex: '', description: '', version: '' });
      setSuccessMessage('Annex has been successfully added.');
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to save annex", err);
      setFailMessage(`Failed to save annex: ${err.message}`);
      setShowFailModal(true);
    }
  };

  const handleSelectAllAnnexes = (e) => {
    if (e.target.checked) {
      setSelectedAnnexIds(annexes.map(a => a.id));
    } else {
      setSelectedAnnexIds([]);
    }
  };

  const handleEditAnnex = () => {
    if (selectedAnnexIds.length !== 1) {
      setFailMessage('Please select exactly one annex to edit.');
      setShowFailModal(true);
      return;
    }
    const selectedId = selectedAnnexIds[0];
    const selectedAnnex = annexes.find(a => a.id === selectedId);
    if (selectedAnnex) {
      setCurrentModal('annex');
      setModalMode('edit');
      setNewAnnex({
        name: selectedAnnex.name,
        code: selectedAnnex.code,
        annex: selectedAnnex.annex,
        description: selectedAnnex.description,
        version: selectedAnnex.version?.id || ''
      });
      const modal = new window.bootstrap.Modal(document.getElementById('modal-form'));
      modal.show();
    }
  };

  const handleDeleteAnnex = () => {
    if (selectedAnnexIds.length === 0) {
      setFailMessage('Please select at least one annex to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        for (const annexId of selectedAnnexIds) {
          await api.delete(`/md/annexes/${annexId}`);
        }
        setSelectedAnnexIds([]);
        await fetchAnnexes();
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected annexes have been successfully deleted.');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete annexes.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more annexes not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  const handleUpdateAnnex = async () => {
    try {
      const selectedId = selectedAnnexIds[0];
      const annexToEdit = annexes.find(a => a.id === selectedId);
      await api.put(`/md/annexes/${selectedId}`, {
        ...annexToEdit,
        name: newAnnex.name,
        code: newAnnex.code,
        annex: newAnnex.annex,
        description: newAnnex.description,
        standard: { id: Number(id) },
        version: newAnnex.version ? { id: Number(newAnnex.version) } : null
      });
      await fetchAnnexes();
      setSelectedAnnexIds([]);
      setNewAnnex({ name: '', code: '', annex: '', description: '', version: '' });
      setSuccessMessage('Annex has been successfully updated.');
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to update annex", err);
      setFailMessage(`Failed to update annex: ${err.message}`);
      setShowFailModal(true);
      throw err;
    }
  };

  const handleAddClausul = () => {
    setCurrentModal('clausul');
    setModalMode('add');
    setNewClausul({ clausul: '', code: '', name: '', description: '', version: '' });
    const modal = new window.bootstrap.Modal(document.getElementById('modal-form'));
    modal.show();
  };

  const handleSaveClausul = async () => {
    try {
      const payload = {
        clausul: newClausul.clausul,
        name: newClausul.name,
        code: newClausul.code,
        description: newClausul.description,
        standard: { id: Number(id) },
        // version: newClausul.version ? { id: Number(newClausul.version) } : null
        standardVersion: newClausul.version ? { id: Number(newClausul.version) } : null
      };
      await api.post(`/md/clauses`, payload);

      // Fetch updated data
      await fetchClausuls();

      setIsAddingClausul(false);
      setNewClausul({ clausul: '', code: '', name: '', description: '', version: '' });
      setSuccessMessage('Clausul has been successfully added.');
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to save clausul", err);
      setFailMessage(`Failed to save clausul: ${err.message}`);
      setShowFailModal(true);
    }
  };

  const handleEditClausul = () => {
    if (selectedClausulIds.length !== 1) {
      setFailMessage('Please select exactly one clausul to edit.');
      setShowFailModal(true);
      return;
    }
    const selectedId = selectedClausulIds[0];
    const selectedClausul = clausuls.find(c => c.id === selectedId);
    if (selectedClausul) {
      setCurrentModal('clausul');
      setModalMode('edit');
      setNewClausul({
        clausul: selectedClausul.clausul,
        code: selectedClausul.code,
        name: selectedClausul.name,
        description: selectedClausul.description,
        version: selectedClausul.version?.id || ''
      });
      const modal = new window.bootstrap.Modal(document.getElementById('modal-form'));
      modal.show();
    }
  };

  const handleDeleteClausul = () => {
    if (selectedClausulIds.length === 0) {
      setFailMessage('Please select at least one clausul to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        for (const clausulId of selectedClausulIds) {
          await api.delete(`/md/clauses/${clausulId}`);
        }
        setSelectedClausulIds([]);
        await fetchClausuls();
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected clausuls have been successfully deleted.');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete clausuls.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more clausuls not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  const handleSelectAllClausuls = (e) => {
    if (e.target.checked) {
      setSelectedClausulIds(clausuls.map(c => c.id));
    } else {
      setSelectedClausulIds([]);
    }
  };

  const handleUpdateClausul = async () => {
    try {
      const selectedId = selectedClausulIds[0];
      const clausulToEdit = clausuls.find(c => c.id === selectedId);
      await api.put(`/md/clauses/${selectedId}`, {
        ...clausulToEdit,
        clausul: newClausul.clausul,
        name: newClausul.name,
        code: newClausul.code,
        description: newClausul.description,
        standard: { id: Number(id) },
        version: newClausul.version ? { id: Number(newClausul.version) } : null
      });
      await fetchClausuls();
      setSelectedClausulIds([]);
      setNewClausul({ clausul: '', code: '', name: '', description: '', version: '' });
      setSuccessMessage('Clausul has been successfully updated.');
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Failed to update clausul", err);
      setFailMessage(`Failed to update clausul: ${err.message}`);
      setShowFailModal(true);
      throw err;
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    try {
      switch (currentModal) {
        case 'version':
          if (modalMode === 'add') {
            await handleSaveVersion();
          } else {
            await handleUpdateVersion();
          }
          break;
        case 'annex':
          if (modalMode === 'add') {
            await handleSaveAnnex();
          } else {
            await handleUpdateAnnex();
          }
          break;
        case 'clausul':
          if (modalMode === 'add') {
            await handleSaveClausul();
          } else {
            await handleUpdateClausul();
          }
          break;
      }

      // Close the modal after successful operation
      const modal = document.getElementById('modal-form');
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    } catch (error) {
      setFailMessage(`Failed to ${modalMode} ${currentModal}: ${error.message}`);
      setShowFailModal(true);
    }
  };

  return (
    <div className="page-wrapper">
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title d-flex align-items-center">
                <a className="me-2 d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); navigate('/masterdata-standard'); }}>
                  <span className="d-flex align-items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M15 6l-6 6l6 6" />
                    </svg>
                  </span>
                </a>
                {standardData.name}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-xl">
          <div className="row row-cards">
            <div className="col-lg-12">
              {/* Basic Information */}

              <div className="card mb-3">
                <div className="card-header">
                  <h3 className="card-title">Standard Detail</h3>
                </div>
                <div className="card-body">
                  <div className="datagrid">

                    <div className="datagrid-item">
                      <div className="datagrid-title">Standard Name</div>
                      <div className="datagrid-content">{standardData.name}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Certification Scheme</div>
                      <div className="datagrid-content">
                        {standardData.certificationScheme?.name || '-'}
                      </div>
                    </div>

                    <div className="datagrid-item">
                      <div className="datagrid-title">Prefix Certificate</div>
                      <div className="datagrid-content">{standardData.prefixCertificate}</div>
                    </div>

                    <div className="datagrid-item">
                      <div className="datagrid-title">Accredited</div>
                      <div className="datagrid-content">{standardData.accredited ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Services</div>
                      <div className="datagrid-content">
                        {Array.isArray(standardData.serviceIds) && standardData.serviceIds.length > 0 ? (
                          standardData.serviceIds.map((serviceId, idx) => {
                            const id = typeof serviceId === 'object' ? serviceId.id : serviceId;
                            const service = servicesMaster.find(s => s.id === Number(id));
                            return (
                              <span key={id || idx} className="badge me-1">
                                {service ? service.name : `Service ${id}`}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-muted">No services</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Version Table */}
              <div className="card mb-3">
                <div className="card-header">
                  <div className="row align-items-center w-full">
                    <div className="col">
                      <h3 className="card-title">Version</h3>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex gap-2">
                        <div className="input-group input-group-flat">
                          <span className="input-group-text">
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <circle cx="10" cy="10" r="7" />
                              <line x1="21" y1="21" x2="15" y2="15" />
                            </svg>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search..."
                            value={versionSearchTerm}
                            onChange={(e) => {
                              setVersionSearchTerm(e.target.value);
                              handleVersionSearchChange(e.target.value);
                            }}
                          />
                          <span className="input-group-text"></span>
                        </div>
                        <div ref={actionsContainerRef} className="dropdown">
                          <a
                            href="#"
                            ref={actionsToggleRef}
                            className="btn dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded={isActionsOpen}
                            onClick={(e) => {
                              e.preventDefault();
                              const inst = actionsDropdownInstanceRef.current;
                              if (inst?.toggle) inst.toggle();
                              else setIsActionsOpen(prev => !prev);
                            }}
                          >Actions</a>
                          <div className="dropdown-menu">
                            <a className="dropdown-item" href="#" onClick={handleAddVersion}>Add</a>
                            <a className="dropdown-item" href="#" onClick={handleEditVersion}>Edit</a>
                            <a className="dropdown-item" href="#" onClick={handleDeleteVersion}>Delete</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table card-table table-vcenter text-nowrap datatable table-hover">
                      <thead>
                        <tr>
                          <th className="w-1">
                            <input
                              className="form-check-input m-0 align-middle"
                              type="checkbox"
                              aria-label="Select all Version"
                              checked={versions.length > 0 && selectedVersionIds.length === versions.length}
                              onChange={handleSelectAllVersions}
                            />
                          </th>
                          <th><button className="table-sort" data-sort="sort-version">Version</button></th>
                          <th className="w-1"></th>
                        </tr>
                      </thead>
                      <tbody className="table-tbody">
                        {versions.map((version, idx) => (
                          <tr key={version.id || idx} className={selectedVersionIds.includes(version.id) ? 'table-active' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedVersionIds.includes(version.id)}
                                onChange={() => {
                                  if (selectedVersionIds.includes(version.id)) {
                                    setSelectedVersionIds(selectedVersionIds.filter(id => id !== version.id));
                                  } else {
                                    setSelectedVersionIds([...selectedVersionIds, version.id]);
                                  }
                                }}
                                className="form-check-input m-0 align-middle"
                              />
                            </td>
                            <td>{version.version || '-'}</td>
                            <td></td>
                          </tr>
                        ))}

                        {isAddingVersion && (
                          <tr>
                            <td><input className="form-check-input m-0 align-middle" type="checkbox" disabled /></td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={newVersion.version}
                                onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
                                placeholder="Enter version"
                              />
                            </td>
                            <td>
                              <div className="btn-list flex-nowrap">
                                <button className="btn btn-success btn-sm" onClick={handleSaveVersion}>
                                  Save
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={() => {
                                  setIsAddingVersion(false);
                                  setNewVersion({ version: '', publishedDate: '', name: '', description: '' });
                                }}>
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Pagination {...versionPagination} />
              </div>

              {/* Annex Table */}
              <div className="card mb-3">
                <div className="card-header">
                  <div className="row align-items-center w-full">
                    <div className="col">
                      <h3 className="card-title">Annex</h3>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex gap-2">
                        <div className="input-group input-group-flat">
                          <span className="input-group-text">
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <circle cx="10" cy="10" r="7" />
                              <line x1="21" y1="21" x2="15" y2="15" />
                            </svg>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search..."
                            value={annexSearchTerm}
                            onChange={(e) => {
                              setAnnexSearchTerm(e.target.value);
                              handleAnnexSearchChange(e.target.value);
                            }}
                          />
                          <span className="input-group-text"></span>
                        </div>
                        <div ref={actionsContainerRef} className="dropdown">
                          <a
                            href="#"
                            ref={actionsToggleRef}
                            className="btn dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded={isActionsOpen}
                            onClick={(e) => {
                              e.preventDefault();
                              const inst = actionsDropdownInstanceRef.current;
                              if (inst?.toggle) inst.toggle();
                              else setIsActionsOpen(prev => !prev);
                            }}
                          >Actions</a>
                          <div className="dropdown-menu">
                            <a className="dropdown-item" href="#" onClick={handleAddAnnex}>Add</a>
                            <a className="dropdown-item" href="#" onClick={handleEditAnnex}>Edit</a>
                            <a className="dropdown-item" href="#" onClick={handleDeleteAnnex}>Delete</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table card-table table-vcenter text-nowrap datatable table-hover">
                      <thead>
                        <tr>
                          <th className="w-1">
                            <input
                              className="form-check-input m-0 align-middle"
                              type="checkbox"
                              aria-label="Select all Annex"
                              checked={annexes.length > 0 && selectedAnnexIds.length === annexes.length}
                              onChange={handleSelectAllAnnexes}
                            />
                          </th>
                          <th><button className="table-sort" data-sort="sort-annex">Annex</button></th>
                          <th><button className="table-sort" data-sort="sort-code">Code</button></th>
                          <th><button className="table-sort" data-sort="sort-name">Name</button></th>
                          <th><button className="table-sort" data-sort="sort-description">Description</button></th>
                          <th><button className="table-sort" data-sort="sort-version">Version</button></th>
                          <th className="w-1"></th>
                        </tr>
                      </thead>
                      <tbody className="table-tbody">
                        {Array.isArray(annexes) && annexes.map((annex, idx) => (
                          <tr key={annex.id || idx} className={selectedAnnexIds.includes(annex.id) ? 'table-active' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedAnnexIds.includes(annex.id)}
                                onChange={() => {
                                  if (selectedAnnexIds.includes(annex.id)) {
                                    setSelectedAnnexIds(selectedAnnexIds.filter(id => id !== annex.id));
                                  } else {
                                    setSelectedAnnexIds([...selectedAnnexIds, annex.id]);
                                  }
                                }}
                                className="form-check-input m-0 align-middle"
                              />
                            </td>
                            <td>{annex.annex || '-'}</td>
                            <td>{annex.code || '-'}</td>
                            <td>{annex.name}</td>
                            <td>{annex.description || '-'}</td>
                            <td>{annex.standardVersion?.version || '-'}</td>
                            <td></td>
                          </tr>
                        ))}

                        {isAddingAnnex && (
                          <tr>
                            <td><input className="form-check-input m-0 align-middle" type="checkbox" disabled /></td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={newAnnex.annex}
                                onChange={(e) => setNewAnnex({ ...newAnnex, annex: e.target.value })}
                                placeholder="Enter annex"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={newAnnex.code}
                                onChange={(e) => setNewAnnex({ ...newAnnex, code: e.target.value })}
                                placeholder="Enter code"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={newAnnex.name}
                                onChange={(e) => setNewAnnex({ ...newAnnex, name: e.target.value })}
                                placeholder="Enter name"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={newAnnex.description}
                                onChange={(e) => setNewAnnex({ ...newAnnex, description: e.target.value })}
                                placeholder="Enter description"
                              />
                            </td>
                            <td>
                              <div className="mb-3">
                                <label className="form-label">Version</label>
                                <select
                                  className="form-select"
                                  value={newAnnex.version}
                                  onChange={(e) => setNewAnnex({ ...newAnnex, version: e.target.value })}
                                >
                                  <option value="">Select Version</option>
                                  {versions.map(version => (
                                    <option key={version.id} value={version.id}>{version.version}</option>
                                  ))}
                                </select>
                              </div>
                            </td>
                            <td>
                              <div className="btn-list flex-nowrap">
                                <button className="btn btn-success btn-sm" onClick={handleSaveAnnex}>
                                  Save
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={() => {
                                  setIsAddingAnnex(false);
                                  setNewAnnex({ annex: '', code: '', name: '', description: '', version: '' });
                                }}>
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Pagination {...annexPagination} />
              </div>


              {/* Clausul Table */}
              <div className="card mb-3">
                <div className="card-header">
                  <div className="row align-items-center w-full">
                    <div className="col">
                      <h3 className="card-title">Clausul</h3>
                    </div>


                    <div className="col-auto d-flex gap-2">
                      <div className="input-group input-group-flat">
                        <span className="input-group-text">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <circle cx="10" cy="10" r="7" />
                            <line x1="21" y1="21" x2="15" y2="15" />
                          </svg>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search..."
                          value={clausulSearchTerm}
                          onChange={(e) => {
                            setClausulSearchTerm(e.target.value);
                            handleClausulSearchChange(e.target.value);
                          }}
                        />
                        <span className="input-group-text"></span>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex gap-2">
                        <div ref={actionsContainerRef} className="dropdown">
                          <a
                            href="#"
                            ref={actionsToggleRef}
                            className="btn dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded={isActionsOpen}
                            onClick={(e) => {
                              e.preventDefault();
                              const inst = actionsDropdownInstanceRef.current;
                              if (inst?.toggle) inst.toggle();
                              else setIsActionsOpen(prev => !prev);
                            }}
                          >Actions</a>
                          <div className="dropdown-menu">
                            <a className="dropdown-item" href="#" onClick={handleAddClausul}>Add</a>
                            <a className="dropdown-item" href="#" onClick={handleEditClausul}>Edit</a>
                            <a className="dropdown-item" href="#" onClick={handleDeleteClausul}>Delete</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table card-table table-vcenter text-nowrap datatable table-hover">
                      <thead>
                        <tr>
                          <th className="w-1"><input
                            className="form-check-input m-0 align-middle"
                            type="checkbox"
                            aria-label="Select all Clausul"
                            checked={clausuls.length > 0 && selectedClausulIds.length === clausuls.length}
                            onChange={handleSelectAllClausuls}
                          /></th>
                          <th><button className="table-sort" data-sort="sort-clausul">Clausul</button></th>
                          <th><button className="table-sort" data-sort="sort-code">Code</button></th>
                          <th><button className="table-sort" data-sort="sort-name">Name</button></th>
                          <th><button className="table-sort" data-sort="sort-description">Description</button></th>
                          <th><button className="table-sort" data-sort="sort-version">Version</button></th>
                          <th className="w-1"></th>
                        </tr>
                      </thead>

                      <tbody className="table-tbody">
                        {clausuls.map((clausul, idx) => (
                          <tr key={clausul.id || idx} className={selectedClausulIds.includes(clausul.id) ? 'table-active' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedClausulIds.includes(clausul.id)}
                                onChange={() => {
                                  if (selectedClausulIds.includes(clausul.id)) {
                                    setSelectedClausulIds(selectedClausulIds.filter(id => id !== clausul.id));
                                  } else {
                                    setSelectedClausulIds([...selectedClausulIds, clausul.id]);
                                  }
                                }}
                                className="form-check-input m-0 align-middle"
                              />
                            </td>
                            <td>{clausul.clausul || '-'}</td>
                            <td>{clausul.code || '-'}</td>
                            <td>{clausul.name}</td>
                            <td>{clausul.description || '-'}</td>
                            <td>{clausul.standardVersion?.version || '-'}</td>
                            <td></td>
                          </tr>
                        ))}

                        {isAddingClausul && (
                          <tr>
                            <td><input className="form-check-input m-0 align-middle" type="checkbox" disabled /></td>
                            <td>
                              <input type="text" className="form-control form-control-sm" value={newClausul.clausul}
                                onChange={(e) => setNewClausul({ ...newClausul, clausul: e.target.value })} placeholder="Enter Clausul" />
                            </td>
                            <td>
                              <input type="text" className="form-control form-control-sm" value={newClausul.code}
                                onChange={(e) => setNewClausul({ ...newClausul, code: e.target.value })} placeholder="Enter code" />
                            </td>
                            <td>
                              <input type="text" className="form-control form-control-sm" value={newClausul.name}
                                onChange={(e) => setNewClausul({ ...newClausul, name: e.target.value })} placeholder="Enter name" />
                            </td>
                            <td>
                              <input type="text" className="form-control form-control-sm" value={newClausul.description}
                                onChange={(e) => setNewClausul({ ...newClausul, description: e.target.value })} placeholder="Enter description" />
                            </td>
                            <td>
                              <div className="mb-3">
                                <label className="form-label">Version</label>
                                <select
                                  className="form-select"
                                  value={newClausul.version}
                                  onChange={(e) => setNewClausul({ ...newClausul, version: e.target.value })}
                                >
                                  <option value="">Select Version</option>
                                  {versions.map(version => (
                                    <option key={version.id} value={version.id}>{version.version}</option>
                                  ))}
                                </select>
                              </div>
                            </td>
                            <td>
                              <div className="btn-list flex-nowrap">
                                <button className="btn btn-success btn-sm" onClick={handleSaveClausul}>Save</button>
                                <button className="btn btn-danger btn-sm" onClick={() => {
                                  setIsAddingClausul(false);
                                  setNewClausul({ clausul: '', code: '', name: '', description: '' });
                                }}>Cancel</button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Pagination {...clausulPagination} />
              </div>


            </div>
          </div>
        </div>
      </div>

      {/* Unified Modal Form */}
      <div className="modal modal-blur fade" id="modal-form" tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalMode === 'add' ? 'Add New' : 'Edit'} {currentModal?.charAt(0).toUpperCase() + currentModal?.slice(1)}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form onSubmit={handleModalSubmit}>
              <div className="modal-body">
                {currentModal === 'version' && (
                  <div className="mb-3">
                    <label className="form-label required">Version</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newVersion.version}
                      onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
                      placeholder="Enter version"
                      required
                    />
                  </div>
                )}

                {currentModal === 'annex' && (
                  <>
                    <div className="mb-3">
                      <label className="form-label required">Annex</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newAnnex.annex}
                        onChange={(e) => setNewAnnex({ ...newAnnex, annex: e.target.value })}
                        placeholder="Enter annex"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label required">Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newAnnex.code}
                        onChange={(e) => setNewAnnex({ ...newAnnex, code: e.target.value })}
                        placeholder="Enter code"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label required">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newAnnex.name}
                        onChange={(e) => setNewAnnex({ ...newAnnex, name: e.target.value })}
                        placeholder="Enter name"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newAnnex.description}
                        onChange={(e) => setNewAnnex({ ...newAnnex, description: e.target.value })}
                        placeholder="Enter description"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label required">Version</label>
                      <select
                        className="form-select"
                        value={newAnnex.version}
                        onChange={(e) => setNewAnnex({ ...newAnnex, version: e.target.value })}
                      >
                        <option value="">Select Version</option>
                        {versions.map(version => (
                          <option key={version.id} value={version.id}>{version.version}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {currentModal === 'clausul' && (
                  <>
                    <div className="mb-3">
                      <label className="form-label required">Clausul</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newClausul.clausul}
                        onChange={(e) => setNewClausul({ ...newClausul, clausul: e.target.value })}
                        placeholder="Enter clausul"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label required">Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newClausul.code}
                        onChange={(e) => setNewClausul({ ...newClausul, code: e.target.value })}
                        placeholder="Enter code"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label required">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newClausul.name}
                        onChange={(e) => setNewClausul({ ...newClausul, name: e.target.value })}
                        placeholder="Enter name"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newClausul.description}
                        onChange={(e) => setNewClausul({ ...newClausul, description: e.target.value })}
                        placeholder="Enter description"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label required">Version</label>
                      <select
                        className="form-select"
                        value={newClausul.version}
                        onChange={(e) => setNewClausul({ ...newClausul, version: e.target.value })}
                      >
                        <option value="">Select Version</option>
                        {versions.map(version => (
                          <option key={version.id} value={version.id}>{version.version}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <a href="#" className="btn btn-link link-secondary" data-bs-dismiss="modal">
                  Cancel
                </a>
                <button type="submit" className="btn btn-primary ms-auto">
                  {modalMode === 'add' ? 'Save' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmAction}
        title="Delete Confirmation"
        message="Are you sure you want to delete the selected items? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      {/* Fail Modal */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </div>
  );
};

export default StandardDetail; 